import React, { useState, useEffect, useRef } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { ArrowRight, Play, ChevronDown, Menu, X, Mail, Phone, Instagram, Youtube, Facebook, Twitter, ExternalLink, Flame, Sparkles, Heart } from 'lucide-react';

// ============================================
// INSPIYAH - Phoenix Fire Edition
// "IGNITE YAH FIYAH!"
// YAH-HEALTH-WEALTH-HAPPINESS (YHWH יהוה) ©
// ============================================

const InspiyahMain = () => {
  const [email, setEmail] = useState('');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [contactForm, setContactForm] = useState({ name: '', email: '', message: '' });
  const heroRef = useRef(null);
  
  const { scrollYProgress } = useScroll();
  const heroOpacity = useTransform(scrollYProgress, [0, 0.15], [1, 0]);
  const heroScale = useTransform(scrollYProgress, [0, 0.15], [1, 1.1]);

  // Animated ember particles
  const EmberParticles = () => (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {[...Array(20)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute w-1 h-1 rounded-full"
          style={{
            background: `radial-gradient(circle, ${i % 2 === 0 ? '#FF6B00' : '#FFD600'} 0%, transparent 70%)`,
            left: `${Math.random() * 100}%`,
            bottom: '-10px',
          }}
          animate={{
            y: [0, -800],
            x: [0, (Math.random() - 0.5) * 200],
            opacity: [0, 1, 0],
            scale: [0, 1.5, 0],
          }}
          transition={{
            duration: 4 + Math.random() * 3,
            repeat: Infinity,
            delay: Math.random() * 5,
            ease: "easeOut"
          }}
        />
      ))}
    </div>
  );

  // Noise texture overlay
  const NoiseOverlay = () => (
    <div 
      className="fixed inset-0 pointer-events-none z-50 opacity-[0.03]"
      style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
      }}
    />
  );

  const handleVIPSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setIsSubmitting(true);
    await new Promise(resolve => setTimeout(resolve, 1500));
    setSubmitted(true);
    setIsSubmitting(false);
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    // Add contact form submission logic
    alert('Message sent! We will get back to you soon.');
    setContactForm({ name: '', email: '', message: '' });
  };

  // Journey cards data
  const journeyCards = [
    { title: "IT'S TIME TO LET", subtitle: "FROM BIRTH", image: "https://static.prod-images.emergentagent.com/jobs/0fb4073d-6247-44e8-be18-a96e363bbb90/images/2e84970df1a2040d3bf4ff5a307aff989f03bc95520d0b574d7e7ac70445d401.png" },
    { title: "YAH SPIRIT", subtitle: "TO DEATH", image: "https://static.prod-images.emergentagent.com/jobs/0fb4073d-6247-44e8-be18-a96e363bbb90/images/2853440965a096bbf189d2754fa99480e79c2c4bbcae95575c44c840d1a10759.png" },
    { title: "SEAR IT'S WORD", subtitle: "TO BIRTH", image: "https://static.prod-images.emergentagent.com/jobs/0fb4073d-6247-44e8-be18-a96e363bbb90/images/e050fd09d92e7a08ee7b69fbda719fa13a0104303f57fb8a7d8ac4fbbb619a3e.png" },
    { title: "ON YAH SOUL.", subtitle: "INSPIYAH IS BORN", image: "https://static.prod-images.emergentagent.com/jobs/0fb4073d-6247-44e8-be18-a96e363bbb90/images/36c1347f6ea9d3b9eb9de262302142a3d127ff8c3b8b0d709ffc771494476f6c.png" },
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white overflow-x-hidden">
      <NoiseOverlay />
      
      {/* ============ NAVIGATION ============ */}
      <nav className="fixed top-0 left-0 right-0 z-40 bg-[#0A0A0A]/90 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <a href="#" className="flex items-center space-x-2" data-testid="nav-logo">
              <Flame className="w-8 h-8 text-[#FF6B00]" />
              <span 
                className="text-2xl font-bold tracking-tighter"
                style={{ 
                  fontFamily: 'Syne, sans-serif',
                  background: 'linear-gradient(135deg, #FF6B00, #FFD600)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}
              >
                INSPIYAH
              </span>
            </a>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center space-x-8">
              <a href="#world" className="text-sm text-white/60 hover:text-[#FF6B00] transition-colors tracking-wide" style={{ fontFamily: 'Manrope, sans-serif' }}>
                WORLD
              </a>
              <a href="#journey" className="text-sm text-white/60 hover:text-[#FF6B00] transition-colors tracking-wide" style={{ fontFamily: 'Manrope, sans-serif' }}>
                JOURNEY
              </a>
              <a href="#culture" className="text-sm text-white/60 hover:text-[#FF6B00] transition-colors tracking-wide" style={{ fontFamily: 'Manrope, sans-serif' }}>
                CULTURE
              </a>
              <a href="#contact" className="text-sm text-white/60 hover:text-[#FF6B00] transition-colors tracking-wide" style={{ fontFamily: 'Manrope, sans-serif' }}>
                CONTACT
              </a>
              <a 
                href="#vip"
                className="px-6 py-2.5 text-black text-sm font-semibold transition-all duration-300 hover:shadow-[0_0_30px_rgba(255,107,0,0.5)]"
                style={{ 
                  fontFamily: 'Manrope, sans-serif',
                  background: 'linear-gradient(135deg, #FF6B00, #FFD600)'
                }}
                data-testid="nav-vip-btn"
              >
                JOIN VIP
              </a>
            </div>

            {/* Mobile Menu Toggle */}
            <button 
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-2 text-[#FF6B00]"
              data-testid="mobile-menu-toggle"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden bg-[#0A0A0A] border-t border-white/5"
            >
              <div className="px-6 py-8 space-y-6">
                <a href="#world" className="block text-lg text-white/80 hover:text-[#FF6B00]" onClick={() => setIsMenuOpen(false)}>World</a>
                <a href="#journey" className="block text-lg text-white/80 hover:text-[#FF6B00]" onClick={() => setIsMenuOpen(false)}>Journey</a>
                <a href="#culture" className="block text-lg text-white/80 hover:text-[#FF6B00]" onClick={() => setIsMenuOpen(false)}>Culture</a>
                <a href="#contact" className="block text-lg text-white/80 hover:text-[#FF6B00]" onClick={() => setIsMenuOpen(false)}>Contact</a>
                <a 
                  href="#vip" 
                  className="block px-6 py-3 text-black text-center font-semibold"
                  style={{ background: 'linear-gradient(135deg, #FF6B00, #FFD600)' }}
                  onClick={() => setIsMenuOpen(false)}
                >
                  JOIN VIP
                </a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* ============ HERO SECTION ============ */}
      <section ref={heroRef} className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Animated Background */}
        <motion.div 
          className="absolute inset-0 z-0"
          style={{ scale: heroScale }}
        >
          {/* Dark base with fire gradient */}
          <div className="absolute inset-0 bg-[#0A0A0A]" />
          <div 
            className="absolute inset-0 opacity-30"
            style={{
              background: 'radial-gradient(ellipse at center bottom, #FF6B00 0%, transparent 60%)'
            }}
          />
          <div 
            className="absolute inset-0 opacity-20"
            style={{
              background: 'radial-gradient(ellipse at 30% 80%, #FFD600 0%, transparent 40%)'
            }}
          />
        </motion.div>

        {/* Ember Particles */}
        <EmberParticles />

        {/* Hero Content */}
        <motion.div 
          className="relative z-10 text-center px-6 max-w-5xl mx-auto"
          style={{ opacity: heroOpacity }}
        >
          {/* YHWH יהוה Badge */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="mb-8"
          >
            <span 
              className="inline-flex items-center px-6 py-2 rounded-full border border-[#FF6B00]/30 bg-[#FF6B00]/10 text-[#FFD600] text-xs tracking-[0.3em] uppercase"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              <Sparkles size={14} className="mr-2" />
              YAH-HEALTH-WEALTH-HAPPINESS (YHWH יהוה) ©
            </span>
          </motion.div>

          {/* Main Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-5xl md:text-7xl lg:text-9xl font-extrabold tracking-tighter leading-[0.85] mb-6"
            style={{ fontFamily: 'Syne, sans-serif' }}
          >
            <span 
              style={{
                background: 'linear-gradient(135deg, #FF6B00 0%, #FFD600 50%, #FF4500 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                filter: 'drop-shadow(0 0 40px rgba(255, 107, 0, 0.5))'
              }}
            >
              IGNITE
            </span>
            <br />
            <span className="text-white">YAH</span>
            <br />
            <span 
              className="relative"
              style={{
                background: 'linear-gradient(135deg, #FFD600 0%, #FF6B00 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              FIYAH!
              {/* Animated flame accent */}
              <motion.span
                className="absolute -right-8 top-0 text-4xl"
                animate={{ 
                  scale: [1, 1.2, 1],
                  rotate: [-5, 5, -5]
                }}
                transition={{ duration: 1.5, repeat: Infinity }}
              >
                🔥
              </motion.span>
            </span>
          </motion.h1>

          {/* Subheadline */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="text-white/60 text-lg md:text-xl max-w-2xl mx-auto mb-10 leading-relaxed"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            Immerse yourself into the World Of INSPIYAH:
            <br />
            <span className="text-[#FF6B00] font-semibold">WELLNESS</span> • 
            <span className="text-[#FFD600] font-semibold ml-2">ART</span> • 
            <span className="text-[#FF6B00] font-semibold ml-2">CULTURE</span> • 
            <span className="text-[#FFD600] font-semibold ml-2">YAH</span>
          </motion.p>

          {/* CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <a 
              href="#vip"
              className="group px-8 py-4 text-black font-semibold text-sm tracking-wide transition-all duration-300 hover:shadow-[0_0_40px_rgba(255,107,0,0.6)] flex items-center space-x-2"
              style={{ 
                fontFamily: 'Manrope, sans-serif',
                background: 'linear-gradient(135deg, #FF6B00, #FFD600)'
              }}
              data-testid="hero-cta-primary"
            >
              <Flame size={18} />
              <span>ENTER THE CAVE</span>
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </a>
            <a 
              href="#world"
              className="px-8 py-4 border border-[#FF6B00]/30 text-[#FF6B00] font-medium text-sm tracking-wide hover:border-[#FF6B00] hover:bg-[#FF6B00]/10 transition-all duration-300"
              style={{ fontFamily: 'Manrope, sans-serif' }}
              data-testid="hero-cta-secondary"
            >
              EXPLORE THE WORLD
            </a>
          </motion.div>
        </motion.div>

        {/* Scroll Indicator */}
        <motion.div 
          className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <ChevronDown size={32} className="text-[#FF6B00]" />
        </motion.div>
      </section>

      {/* ============ FORMULA SECTION ============ */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        <div className="max-w-6xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            {/* Four Pillars */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
              {[
                { word: 'YAH', color: '#FF6B00' },
                { word: 'HEALTH', color: '#FFD600' },
                { word: 'WEALTH', color: '#FF6B00' },
                { word: 'HAPPINESS', color: '#FFD600' },
              ].map((item, index) => (
                <motion.div
                  key={item.word}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="p-6 border border-white/10 bg-white/[0.02] hover:border-[#FF6B00]/50 transition-all duration-300 group"
                >
                  <Flame size={24} className="mx-auto mb-3 text-[#FF6B00] group-hover:scale-110 transition-transform" />
                  <span 
                    className="text-xl md:text-2xl font-bold tracking-tight"
                    style={{ fontFamily: 'Syne, sans-serif', color: item.color }}
                  >
                    {item.word}
                  </span>
                </motion.div>
              ))}
            </div>

            {/* Bridge Statement */}
            <div className="max-w-3xl mx-auto p-8 border border-[#FF6B00]/20 bg-gradient-to-br from-[#FF6B00]/5 to-transparent">
              <p 
                className="text-white/80 text-lg md:text-xl leading-relaxed"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                We are simply a <span className="text-[#FF6B00] font-semibold">bridge</span>. At INSPIYAH we are here to unlock the journey of the well spirit, or <span className="text-[#FFD600] font-semibold">YAH spirit</span>. We represent the embodiment of faith, determination, and resilience. We are not just a brand we are a lifestyle.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ============ WORLD OF INSPIYAH ============ */}
      <section id="world" className="relative py-24 md:py-32 overflow-hidden">
        {/* Diagonal gradient accent */}
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            background: 'linear-gradient(135deg, transparent 40%, #FF6B00 50%, transparent 60%)'
          }}
        />

        <div className="relative z-10 max-w-6xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="grid lg:grid-cols-2 gap-12 items-center"
          >
            {/* Text Content */}
            <div>
              <span 
                className="text-[#FFD600] text-xs tracking-[0.3em] uppercase block mb-4"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                The Portal
              </span>
              
              <h2 
                className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[0.95] mb-6"
                style={{ fontFamily: 'Syne, sans-serif' }}
              >
                WORLD Of
                <br />
                <span 
                  style={{
                    background: 'linear-gradient(135deg, #FF6B00, #FFD600)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  INSPIYAH
                </span>
              </h2>

              <p 
                className="text-white/60 text-lg leading-relaxed mb-6"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                "STEP THROUGH THE PORTAL, IGNITE YAH SPIRIT & INSPIYAH"
              </p>

              <p 
                className="text-white/50 text-base leading-relaxed mb-8"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                Welcome to the WORLD Of INSPIYAH© — a symbol of praise to YAH's name. As we find ourselves in the daily rat race of mental, physical and spiritual bondage, The World of INSPIYAH represents a portal to our inner being aka "Yah Spirit."
              </p>

              <a 
                href="https://worldofinspiyah.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center space-x-3 px-8 py-4 text-black font-semibold hover:shadow-[0_0_30px_rgba(255,107,0,0.5)] transition-all duration-300"
                style={{ 
                  fontFamily: 'Manrope, sans-serif',
                  background: 'linear-gradient(135deg, #FF6B00, #FFD600)'
                }}
                data-testid="world-cta"
              >
                <span>ENTER THE WORLD</span>
                <ExternalLink size={18} />
              </a>
            </div>

            {/* Visual */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div 
                className="aspect-square rounded-full flex items-center justify-center"
                style={{
                  background: 'radial-gradient(circle, #FF6B00 0%, transparent 70%)',
                  boxShadow: '0 0 100px rgba(255, 107, 0, 0.4)'
                }}
              >
                <div className="w-3/4 h-3/4 rounded-full border-2 border-[#FFD600]/30 flex items-center justify-center">
                  <div className="w-2/3 h-2/3 rounded-full border-2 border-[#FF6B00]/30 flex items-center justify-center">
                    <span 
                      className="text-6xl md:text-8xl font-extrabold"
                      style={{ 
                        fontFamily: 'Syne, sans-serif',
                        background: 'linear-gradient(135deg, #FFD600, #FF6B00)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                      }}
                    >
                      W
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ============ JOURNEY CARDS ============ */}
      <section id="journey" className="relative py-24 md:py-32 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <span 
              className="text-[#FFD600] text-xs tracking-[0.3em] uppercase block mb-4"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              The EPIIC Journey
            </span>
            <h2 
              className="text-3xl md:text-5xl font-extrabold tracking-tight"
              style={{ fontFamily: 'Syne, sans-serif' }}
            >
              FROM <span className="text-[#FF6B00]">BIRTH</span> TO <span className="text-[#FFD600]">INSPIYAH</span>
            </h2>
          </motion.div>

          {/* Journey Cards Grid */}
          <div className="grid md:grid-cols-4 gap-6">
            {journeyCards.map((card, index) => (
              <motion.div
                key={card.subtitle}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                className="group relative"
              >
                <div 
                  className="relative overflow-hidden border-2 border-[#FF6B00]/20 hover:border-[#FF6B00] transition-all duration-500"
                  style={{ aspectRatio: '3/4' }}
                >
                  {/* Image */}
                  <img 
                    src={card.image}
                    alt={card.title}
                    className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500 group-hover:scale-105"
                  />
                  
                  {/* Overlay - stronger gradient for text readability */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/70" />
                  
                  {/* Orange accent frame */}
                  <div className="absolute top-4 left-4 right-4 bottom-4 border border-[#FF6B00]/50 group-hover:border-[#FFD600] transition-colors" />
                  
                  {/* Content */}
                  <div className="absolute inset-0 flex flex-col justify-between p-6">
                    <div>
                      <span 
                        className="text-[#FFD600] text-xs tracking-[0.2em] uppercase font-bold drop-shadow-[0_2px_4px_rgba(0,0,0,0.9)]"
                        style={{ fontFamily: 'Manrope, sans-serif', textShadow: '0 2px 8px rgba(0,0,0,0.95), 0 0 20px rgba(0,0,0,0.8)' }}
                      >
                        YAH HEALTH WEALTH HAPPINESS
                      </span>
                    </div>
                    <div>
                      <h3 
                        className="text-xl md:text-2xl font-bold text-white leading-tight mb-2"
                        style={{ fontFamily: 'Syne, sans-serif' }}
                      >
                        {card.title}
                      </h3>
                      <p 
                        className="text-[#FF6B00] text-sm font-semibold tracking-wider uppercase"
                        style={{ fontFamily: 'Manrope, sans-serif' }}
                      >
                        {card.subtitle}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Arrow Timeline */}
          <div className="hidden md:flex items-center justify-center mt-8 space-x-4">
            {['BIRTH', '→', 'DEATH', '→', 'REBIRTH', '→', 'INSPIYAH'].map((item, i) => (
              <span 
                key={i}
                className={`text-sm font-semibold ${item === '→' ? 'text-[#FF6B00]' : 'text-white/60'}`}
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                {item}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ============ CAVE OF TREASURES - VIP SIGNUP ============ */}
      <section id="vip" className="relative py-24 md:py-32 overflow-hidden">
        {/* Fire background */}
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1475924156734-496f6cac6ec1?w=1920&q=80')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'brightness(0.2) saturate(1.5)'
          }}
        />
        <div 
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(to bottom, #0A0A0A 0%, transparent 30%, transparent 70%, #0A0A0A 100%)'
          }}
        />

        <EmberParticles />

        <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            {/* Phoenix Icon */}
            <motion.div 
              className="w-24 h-24 mx-auto mb-8 rounded-full flex items-center justify-center"
              style={{
                background: 'linear-gradient(135deg, #FF6B00, #FFD600)',
                boxShadow: '0 0 60px rgba(255, 107, 0, 0.5)'
              }}
              animate={{ 
                boxShadow: [
                  '0 0 60px rgba(255, 107, 0, 0.5)',
                  '0 0 80px rgba(255, 214, 0, 0.6)',
                  '0 0 60px rgba(255, 107, 0, 0.5)'
                ]
              }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <Flame size={48} className="text-black" />
            </motion.div>

            <h2 
              className="text-3xl md:text-5xl font-extrabold tracking-tight mb-4"
              style={{ fontFamily: 'Syne, sans-serif' }}
            >
              ENTER THE <span style={{
                background: 'linear-gradient(135deg, #FF6B00, #FFD600)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}>CAVE OF TREASURES</span>
            </h2>
            
            <p 
              className="text-white/60 text-lg mb-2"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              PHOENIX RISING VIP LIST
            </p>
            
            <p 
              className="text-white/40 text-sm max-w-lg mx-auto mb-10"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Be the first to get notified on the YAH SPIRIT + STREETWEAR INSPIYAH CULTURE DRIP DROP
            </p>

            {/* Email Form */}
            {!submitted ? (
              <form onSubmit={handleVIPSubmit} className="max-w-md mx-auto">
                <div className="relative group">
                  {/* Animated glow border */}
                  <motion.div 
                    className="absolute -inset-[2px] rounded-sm opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity duration-300"
                    style={{
                      background: 'linear-gradient(135deg, #FF6B00, #FFD600, #FF6B00)',
                      filter: 'blur(4px)'
                    }}
                    animate={{
                      background: [
                        'linear-gradient(135deg, #FF6B00, #FFD600, #FF6B00)',
                        'linear-gradient(135deg, #FFD600, #FF6B00, #FFD600)',
                        'linear-gradient(135deg, #FF6B00, #FFD600, #FF6B00)',
                      ]
                    }}
                    transition={{ duration: 3, repeat: Infinity }}
                  />
                  
                  <div className="relative flex bg-[#0A0A0A] border border-[#FF6B00]/30 overflow-hidden">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your email"
                      className="flex-1 px-6 py-4 bg-transparent text-white placeholder-white/30 focus:outline-none text-base"
                      style={{ fontFamily: 'Manrope, sans-serif' }}
                      data-testid="vip-email-input"
                      required
                    />
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="px-6 py-4 text-black font-semibold transition-all disabled:opacity-50 flex items-center space-x-2"
                      style={{ 
                        fontFamily: 'Manrope, sans-serif',
                        background: 'linear-gradient(135deg, #FF6B00, #FFD600)'
                      }}
                      data-testid="vip-submit-btn"
                    >
                      {isSubmitting ? (
                        <motion.span animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity }}>
                          <Flame size={20} />
                        </motion.span>
                      ) : (
                        <>
                          <span className="hidden sm:inline">IGNITE</span>
                          <ArrowRight size={20} />
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </form>
            ) : (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="max-w-md mx-auto p-6 border border-[#FF6B00]/30"
                style={{
                  background: 'linear-gradient(135deg, rgba(255,107,0,0.1), rgba(255,214,0,0.05))'
                }}
              >
                <Flame size={32} className="mx-auto mb-3 text-[#FFD600]" />
                <p className="text-[#FFD600] text-lg font-semibold" style={{ fontFamily: 'Syne, sans-serif' }}>
                  FIYAH IGNITED! 🔥
                </p>
                <p className="text-white/60 text-sm mt-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  You're now on the Phoenix Rising VIP list.
                </p>
              </motion.div>
            )}
          </motion.div>
        </div>
      </section>

      {/* ============ INSPIYAH CULTURE SECTION ============ */}
      <section id="culture" className="relative py-24 md:py-32 bg-[#050505]">
        <div className="max-w-6xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Visual */}
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div 
                className="aspect-square border-2 border-[#FF6B00]/30 p-8 flex items-center justify-center"
                style={{
                  background: 'linear-gradient(135deg, rgba(255,107,0,0.05), transparent)'
                }}
              >
                <div className="text-center">
                  <span 
                    className="text-6xl md:text-8xl font-extrabold block"
                    style={{ 
                      fontFamily: 'Syne, sans-serif',
                      background: 'linear-gradient(135deg, #FF6B00, #FFD600)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }}
                  >
                    888
                  </span>
                  <span 
                    className="text-white/40 text-sm tracking-[0.2em] uppercase mt-4 block"
                    style={{ fontFamily: 'Manrope, sans-serif' }}
                  >
                    Welcome to Heaven's Gate
                  </span>
                </div>
              </div>
            </motion.div>

            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <span 
                className="text-[#FFD600] text-xs tracking-[0.3em] uppercase block mb-4"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                Coming Soon
              </span>
              
              <h2 
                className="text-3xl md:text-5xl font-extrabold tracking-tight leading-tight mb-6"
                style={{ fontFamily: 'Syne, sans-serif' }}
              >
                INSPIYAH IS NOT
                <br />
                JUST A BRAND
                <br />
                <span className="text-[#FF6B00]">IT'S A CULTURE!</span>
              </h2>

              <p 
                className="text-white/60 text-lg leading-relaxed mb-6"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                <span className="text-[#FFD600] font-semibold">INSPIYAH CULTURE = YAH SPIRIT + STREETWEAR</span>
              </p>

              <p 
                className="text-white/50 text-base leading-relaxed mb-8"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                The INSPIYAH CULTURE is a brand of faith-based streetwear fashion. Floss the essential DRIP TO SCULPTURE THE INSPIYAH CULTURE. It's time to proceed further on the next step of your INSPIYAH journey.
              </p>

              <a 
                href="https://www.inspiyahculture.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center space-x-3 px-8 py-4 text-black font-semibold hover:shadow-[0_0_30px_rgba(255,107,0,0.5)] transition-all duration-300"
                style={{ 
                  fontFamily: 'Manrope, sans-serif',
                  background: 'linear-gradient(135deg, #FF6B00, #FFD600)'
                }}
                data-testid="culture-cta"
              >
                <span>EXPLORE CULTURE</span>
                <ArrowRight size={18} />
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ============ SCRIPTURE BANNER ============ */}
      <section className="relative py-20 overflow-hidden">
        <div 
          className="absolute inset-0 opacity-10"
          style={{
            background: 'linear-gradient(90deg, #FF6B00, #FFD600, #FF6B00)'
          }}
        />
        
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1 }}
          >
            <p 
              className="text-xl md:text-2xl lg:text-3xl text-white/80 italic leading-relaxed mb-6"
              style={{ fontFamily: 'Cinzel Decorative, serif' }}
            >
              "Do not be afraid for I am with you, do not be anxious because I am your GOD. I will fortify you, yes I will help you."
            </p>
            <p 
              className="text-[#FFD600] text-sm tracking-[0.2em] uppercase"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              — YHWH יהוה Isaiah 41:10
            </p>
          </motion.div>
        </div>
      </section>

      {/* ============ CONTACT SECTION ============ */}
      <section id="contact" className="relative py-24 md:py-32 bg-[#050505]">
        <div className="max-w-6xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Info */}
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <span 
                className="text-[#FFD600] text-xs tracking-[0.3em] uppercase block mb-4"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                Get In Touch
              </span>
              
              <h2 
                className="text-3xl md:text-5xl font-extrabold tracking-tight mb-6"
                style={{ fontFamily: 'Syne, sans-serif' }}
              >
                CONTACT <span className="text-[#FF6B00]">US</span>
              </h2>

              <p 
                className="text-white/60 text-lg leading-relaxed mb-8"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                Peace to the Godz. Thank you for your interest in "INSPIYAH." We would love to hear from you.
              </p>

              {/* Contact Info */}
              <div className="space-y-4 mb-8">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 border border-[#FF6B00]/30 flex items-center justify-center">
                    <Phone size={20} className="text-[#FF6B00]" />
                  </div>
                  <div>
                    <p className="text-white/40 text-xs uppercase tracking-wider" style={{ fontFamily: 'Manrope, sans-serif' }}>Call Us</p>
                    <p className="text-white text-lg" style={{ fontFamily: 'Syne, sans-serif' }}>+1.84.INSPIYAH</p>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 border border-[#FF6B00]/30 flex items-center justify-center">
                    <Mail size={20} className="text-[#FF6B00]" />
                  </div>
                  <div>
                    <p className="text-white/40 text-xs uppercase tracking-wider" style={{ fontFamily: 'Manrope, sans-serif' }}>Email</p>
                    <p className="text-white text-lg" style={{ fontFamily: 'Syne, sans-serif' }}>vision@inspiyah.com</p>
                  </div>
                </div>
              </div>

              {/* Social Links */}
              <div className="flex items-center space-x-3">
                {[
                  { icon: Facebook, href: 'https://www.facebook.com/inspiyah' },
                  { icon: Instagram, href: 'https://www.instagram.com/inspiyah_wellness/' },
                  { icon: Youtube, href: 'https://www.youtube.com/@inspiyah/' },
                  { icon: Twitter, href: 'https://www.x.com/inspiyah' },
                ].map((social, i) => (
                  <a 
                    key={i}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 border border-[#FF6B00]/30 flex items-center justify-center hover:border-[#FF6B00] hover:bg-[#FF6B00]/10 transition-all duration-300"
                    data-testid={`social-${i}`}
                  >
                    <social.icon size={20} className="text-[#FF6B00]" />
                  </a>
                ))}
              </div>
            </motion.div>

            {/* Form */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <form onSubmit={handleContactSubmit} className="space-y-6">
                <div>
                  <label className="text-white/40 text-xs uppercase tracking-wider block mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Name
                  </label>
                  <input
                    type="text"
                    value={contactForm.name}
                    onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                    className="w-full px-6 py-4 bg-white/5 border border-[#FF6B00]/30 text-white placeholder-white/30 focus:outline-none focus:border-[#FF6B00] transition-colors"
                    style={{ fontFamily: 'Manrope, sans-serif' }}
                    required
                    data-testid="contact-name"
                  />
                </div>
                <div>
                  <label className="text-white/40 text-xs uppercase tracking-wider block mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Email *
                  </label>
                  <input
                    type="email"
                    value={contactForm.email}
                    onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                    className="w-full px-6 py-4 bg-white/5 border border-[#FF6B00]/30 text-white placeholder-white/30 focus:outline-none focus:border-[#FF6B00] transition-colors"
                    style={{ fontFamily: 'Manrope, sans-serif' }}
                    required
                    data-testid="contact-email"
                  />
                </div>
                <div>
                  <label className="text-white/40 text-xs uppercase tracking-wider block mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Message
                  </label>
                  <textarea
                    value={contactForm.message}
                    onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                    rows={5}
                    className="w-full px-6 py-4 bg-white/5 border border-[#FF6B00]/30 text-white placeholder-white/30 focus:outline-none focus:border-[#FF6B00] transition-colors resize-none"
                    style={{ fontFamily: 'Manrope, sans-serif' }}
                    data-testid="contact-message"
                  />
                </div>
                <button
                  type="submit"
                  className="w-full py-4 text-black font-semibold hover:shadow-[0_0_30px_rgba(255,107,0,0.5)] transition-all duration-300 flex items-center justify-center space-x-2"
                  style={{ 
                    fontFamily: 'Manrope, sans-serif',
                    background: 'linear-gradient(135deg, #FF6B00, #FFD600)'
                  }}
                  data-testid="contact-submit"
                >
                  <span>SEND MESSAGE</span>
                  <ArrowRight size={18} />
                </button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ============ FOOTER ============ */}
      <footer className="relative py-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          {/* Giant Brand Name */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1 }}
            className="text-center mb-16"
          >
            <h2 
              className="text-[15vw] font-extrabold tracking-tighter leading-none"
              style={{ 
                fontFamily: 'Syne, sans-serif',
                background: 'linear-gradient(135deg, rgba(255,107,0,0.1), rgba(255,214,0,0.05))',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              INSPIYAH
            </h2>
          </motion.div>

          {/* Footer Links */}
          <div className="flex flex-wrap justify-center gap-8 mb-12">
            <a href="#world" className="text-white/40 hover:text-[#FF6B00] text-sm transition-colors" style={{ fontFamily: 'Manrope, sans-serif' }}>World</a>
            <a href="#journey" className="text-white/40 hover:text-[#FF6B00] text-sm transition-colors" style={{ fontFamily: 'Manrope, sans-serif' }}>Journey</a>
            <a href="#culture" className="text-white/40 hover:text-[#FF6B00] text-sm transition-colors" style={{ fontFamily: 'Manrope, sans-serif' }}>Culture</a>
            <a href="#vip" className="text-white/40 hover:text-[#FF6B00] text-sm transition-colors" style={{ fontFamily: 'Manrope, sans-serif' }}>VIP</a>
            <a href="#contact" className="text-white/40 hover:text-[#FF6B00] text-sm transition-colors" style={{ fontFamily: 'Manrope, sans-serif' }}>Contact</a>
          </div>

          {/* Social Icons */}
          <div className="flex justify-center space-x-4 mb-12">
            {[Facebook, Instagram, Youtube, Twitter].map((Icon, i) => (
              <a 
                key={i}
                href="#"
                className="w-10 h-10 border border-[#FF6B00]/30 flex items-center justify-center hover:border-[#FF6B00] hover:bg-[#FF6B00]/10 transition-all duration-300"
              >
                <Icon size={18} className="text-[#FF6B00]" />
              </a>
            ))}
          </div>

          {/* Copyright */}
          <div className="text-center">
            <p 
              className="text-white/20 text-xs"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              © {new Date().getFullYear()} INSPIYAH. All rights reserved.
              <br />
              <span className="text-[#FF6B00]/40">YAH-HEALTH-WEALTH-HAPPINESS (YHWH יהוה) ©</span>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default InspiyahMain;
